// ─── Pet (follower Surggie companion) ─────────────────────────────────────────

import { Direction, TILE_SIZE, type Position } from "./constants";
import type { Camera } from "./camera";
import { getCachedSprite, loadSprite } from "./sprite-sheet";

const PET_SIZE = 14; // legacy footprint — used by the procedural fallback
const HISTORY_MAX = 300;

// char_* sheet geometry: 64x128 = 4 anim cols x 4 direction rows, 16x32 frames.
const CELL_W = 16;
const CELL_H = 32;
const ANIM_COLS = 4;

// Sheet row order is down/left/right/up — NOT the Direction enum order
// (Up=0, Down=1, Left=2, Right=3), so map explicitly.
const DIR_ROW: Record<Direction, number> = {
  [Direction.Down]: 0,
  [Direction.Left]: 1,
  [Direction.Right]: 2,
  [Direction.Up]: 3,
};

// Followers must trail the player — never stand on top of them.
const MIN_FOLLOW_DIST = TILE_SIZE * 1.5; // 24px

// Walk-cycle cadence in game frames per animation column.
const WALK_FRAME_TICKS = 8;
const ENERGETIC_FRAME_TICKS = 6;

// Palette (PIXEL_STYLE.md) — no pure black/white.
const INK = "#1a1a1a";
const CREAM = "#f5ecd6";

interface HuskyColors {
  body: string;
  saddle: string;
  belly: string;
}

export class Pet {
  position: Position;
  direction: Direction = Direction.Down;
  isMoving: boolean = false;

  legendary: boolean;
  energetic: boolean;
  shy: boolean;

  readonly spriteKey: string;

  private name: string;

  /** Read-only accessor so interaction/UI code can identify this follower. */
  get displayName(): string {
    return this.name;
  }

  private followDelay: number;
  private colors: HuskyColors;
  private animTick: number = 0;
  private animFrame: number = 0;
  private lateralOffsetX: number = 0;
  private lateralOffsetY: number = 0;
  private idleSwayPhase: number = 0;
  private spriteRequested: boolean = false;

  constructor(
    x: number,
    y: number,
    name: string,
    followDelay: number,
    colors: HuskyColors,
    legendary: boolean = false,
    lateralOffsetX: number = 0,
    lateralOffsetY: number = 0,
    energetic: boolean = false,
    shy: boolean = false,
    spriteKey: string = "surggie_1",
  ) {
    this.position = { x, y };
    this.name = name;
    this.followDelay = followDelay;
    this.colors = colors;
    this.legendary = legendary;
    this.lateralOffsetX = lateralOffsetX;
    this.lateralOffsetY = lateralOffsetY;
    this.energetic = energetic;
    this.shy = shy;
    this.spriteKey = spriteKey;
  }

  update(_dt: number, playerHistory: Position[], allPets: Pet[] = []): void {
    if (playerHistory.length === 0) return;

    const animRate = this.energetic ? ENERGETIC_FRAME_TICKS : WALK_FRAME_TICKS;
    const shyExtraDelay = this.shy ? 8 : 0;
    const histIdx = Math.max(0, playerHistory.length - 1 - this.followDelay - shyExtraDelay);
    const rawTarget = playerHistory[histIdx];
    const playerPos = playerHistory[playerHistory.length - 1];

    // Enforce a minimum trailing distance: when the player idles, the trail
    // history converges onto their position and every follower used to pile
    // on top of them. Push the target back out to a ring around the player.
    let tx = rawTarget.x;
    let ty = rawTarget.y;
    const pdx = tx - playerPos.x;
    const pdy = ty - playerPos.y;
    const pdist = Math.sqrt(pdx * pdx + pdy * pdy);
    if (pdist < MIN_FOLLOW_DIST) {
      if (pdist > 0.01) {
        // Push back along the trail direction — lead follower stays behind.
        tx = playerPos.x + (pdx / pdist) * MIN_FOLLOW_DIST;
        ty = playerPos.y + (pdy / pdist) * MIN_FOLLOW_DIST;
      } else {
        // History fully converged (e.g. fresh spawn): back off along the
        // pet's own bearing from the player so the pack rings naturally.
        const bx = this.position.x - playerPos.x;
        const by = this.position.y - playerPos.y;
        const bd = Math.sqrt(bx * bx + by * by);
        if (bd > 0.01) {
          tx = playerPos.x + (bx / bd) * MIN_FOLLOW_DIST;
          ty = playerPos.y + (by / bd) * MIN_FOLLOW_DIST;
        } else {
          tx = playerPos.x;
          ty = playerPos.y + MIN_FOLLOW_DIST;
        }
      }
    }

    // Always apply lateral offset to target for fan formation
    const target = {
      x: tx + this.lateralOffsetX,
      y: ty + this.lateralOffsetY,
    };

    const dx = target.x - this.position.x;
    const dy = target.y - this.position.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist > 3) {
      this.isMoving = true;
      const speed = Math.min(dist * 0.25, 3.5);
      this.position.x += (dx / dist) * speed;
      this.position.y += (dy / dist) * speed;

      // Direction from movement vector — dominant axis wins.
      if (Math.abs(dx) > Math.abs(dy)) {
        this.direction = dx > 0 ? Direction.Right : Direction.Left;
      } else {
        this.direction = dy > 0 ? Direction.Down : Direction.Up;
      }

      this.animTick++;
      if (this.animTick >= animRate) {
        this.animTick = 0;
        this.animFrame = (this.animFrame + 1) % ANIM_COLS;
      }
    } else {
      this.isMoving = false;
      this.animFrame = 0;
      this.idleSwayPhase += 0.04;
    }

    // Stronger separation force — wider threshold, bigger push
    const SEPARATION_DIST = 28;
    const SEPARATION_FORCE = 0.25;
    for (const other of allPets) {
      if (other === this) continue;
      const ox = this.position.x - other.position.x;
      const oy = this.position.y - other.position.y;
      const od = Math.sqrt(ox * ox + oy * oy);
      if (od < SEPARATION_DIST && od > 0) {
        const push = (SEPARATION_DIST - od) * SEPARATION_FORCE;
        this.position.x += (ox / od) * push;
        this.position.y += (oy / od) * push;
      }
    }
  }

  draw(ctx: CanvasRenderingContext2D, _camera: Camera): void {
    // SurggieState.spriteKey appears both with and without the "char_"
    // manifest prefix across the codebase — normalize to the cache key.
    const key = this.spriteKey.startsWith("char_")
      ? this.spriteKey
      : `char_${this.spriteKey}`;
    const img = getCachedSprite(key);

    if (!img && !this.spriteRequested) {
      this.spriteRequested = true;
      // Fire-and-forget — the procedural fallback covers us until it lands.
      void loadSprite(key).catch(() => {});
    }

    // Idle bob: whole-pixel only — fractional offsets blur pixel art.
    const bob = !this.isMoving && Math.sin(this.idleSwayPhase) > 0 ? 1 : 0;

    if (img) {
      this.drawSprite(ctx, img, bob);
    } else {
      this.drawFallback(ctx, bob);
    }

    if (!this.isMoving) {
      this.drawNameTag(ctx);
    }
  }

  private drawSprite(
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement,
    bob: number,
  ): void {
    const dx = Math.round(this.position.x);
    const dy = Math.round(this.position.y) - bob;

    // Ground shadow under the feet — flat ink ellipse, no blur.
    ctx.save();
    ctx.globalAlpha = this.legendary ? 0.16 : 0.25;
    ctx.fillStyle = INK;
    ctx.beginPath();
    ctx.ellipse(
      dx + CELL_W / 2,
      Math.round(this.position.y) + CELL_H - 2,
      7,
      3,
      0,
      0,
      Math.PI * 2,
    );
    ctx.fill();
    ctx.restore();

    const row = DIR_ROW[this.direction];
    const col = this.isMoving ? this.animFrame % ANIM_COLS : 0;

    if (this.legendary) {
      ctx.save();
      ctx.globalAlpha = 0.7; // spirit/ghost translucency
    }
    ctx.drawImage(
      img,
      col * CELL_W,
      row * CELL_H,
      CELL_W,
      CELL_H,
      dx,
      dy,
      CELL_W,
      CELL_H,
    );
    if (this.legendary) {
      ctx.restore();
    }
  }

  /**
   * Simplified procedural fallback (legacy rectangle husky) for the frames
   * before the char_* sheet finishes loading. Positioned in the lower half
   * of the 16x32 cell so it shares the sprite's ground line.
   */
  private drawFallback(ctx: CanvasRenderingContext2D, bob: number): void {
    const px = Math.round(this.position.x);
    const py = Math.round(this.position.y);
    const flip = this.direction === Direction.Right;
    const { body, saddle, belly } = this.colors;

    ctx.save();
    ctx.translate(px + CELL_W / 2, py + CELL_H - PET_SIZE / 2 - bob);
    if (flip) ctx.scale(-1, 1);

    // Ground shadow
    ctx.globalAlpha = 0.25;
    ctx.fillStyle = INK;
    ctx.beginPath();
    ctx.ellipse(0, 6, 8, 3, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.globalAlpha = this.legendary ? 0.6 : 1;

    // Legs (simple swing)
    const legSwing = this.isMoving ? Math.sin(this.animFrame * (Math.PI / 2)) * 2 : 0;
    ctx.fillStyle = body;
    ctx.fillRect(-5, 3 + legSwing, 3, 4);
    ctx.fillRect(2, 3 - legSwing, 3, 4);

    // Body
    ctx.fillStyle = body;
    ctx.fillRect(-7, -3, 14, 7);

    // Belly
    ctx.fillStyle = belly;
    ctx.fillRect(-5, 1, 10, 3);

    // Saddle (back marking)
    ctx.fillStyle = saddle;
    ctx.fillRect(-4, -4, 9, 3);

    // Head
    ctx.fillStyle = body;
    ctx.fillRect(-10, -7, 6, 6);
    ctx.fillStyle = saddle;
    ctx.fillRect(-10, -7, 6, 2);

    // Eye (ink per character rules)
    ctx.fillStyle = INK;
    ctx.fillRect(-8, -4, 1, 1);

    ctx.restore();
  }

  private drawNameTag(ctx: CanvasRenderingContext2D): void {
    const cx = Math.round(this.position.x) + CELL_W / 2;
    const top = Math.round(this.position.y);
    const tw = this.name.length * 4 + 6;

    ctx.save();
    ctx.fillStyle = "rgba(26, 26, 26, 0.65)";
    ctx.beginPath();
    ctx.roundRect(cx - tw / 2, top - 12, tw, 9, 3);
    ctx.fill();
    ctx.fillStyle = CREAM;
    ctx.font = "bold 6px monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(this.name, cx, top - 7);
    ctx.restore();
  }

  getCenter(): Position {
    return {
      x: this.position.x + CELL_W / 2,
      y: this.position.y + CELL_H / 2,
    };
  }

  /**
   * Depth-sort key. Pets now share the character coordinate convention
   * (position = top-left of a 16x32 cell), and the renderer interleaves
   * pets and characters by comparing sortY against character.position.y.
   */
  get sortY(): number {
    return this.position.y;
  }
}

// ─── Factory ──────────────────────────────────────────────────────────────────

// Los Lobos Mafios — the real pack.
// Fallback colors are drawn from the 16-color master palette (PIXEL_STYLE.md);
// they only show for the frames before the char_* sheets load.
export function createHuskies(
  startX: number,
  startY: number,
  spriteKeys: string[] = [],
): Pet[] {
  return [
    // Onyx — deep dark son (lead, center — trails closest to the player)
    new Pet(startX, startY, "Onyx", 14, {
      body: "#5c4a30",
      saddle: "#1a1a1a",
      belly: "#8a7a5c",
    }, false, 0, 0, true, false, spriteKeys[0] ?? "surggie_1"),
    // Rico — brown/copper son (behind-left)
    new Pet(startX - 24, startY + 14, "Rico", 26, {
      body: "#8a7a5c",
      saddle: "#5c4a30",
      belly: "#f5ecd6",
    }, false, -24, 14, false, true, spriteKeys[1] ?? "surggie_2"),
    // Crystal — ice blue-white mom (behind-right)
    new Pet(startX + 24, startY + 14, "Crystal", 38, {
      body: "#a8d2e8",
      saddle: "#6a9bc4",
      belly: "#f5ecd6",
    }, false, 24, 14, true, false, spriteKeys[2] ?? "surggie_3"),
    // Ruby — reddish-copper daughter (far left)
    new Pet(startX - 22, startY - 10, "Ruby", 50, {
      body: "#c54a4a",
      saddle: "#5c4a30",
      belly: "#f5ecd6",
    }, false, -36, -10, false, true, spriteKeys[3] ?? "surggie_4"),
    // Cratus — legendary spirit dad (far right)
    new Pet(startX + 22, startY - 10, "Cratus", 62, {
      body: "#c5a83a",
      saddle: "#8a7a5c",
      belly: "#f5ecd6",
    }, true, 36, -10, true, false, spriteKeys[4] ?? "surggie_5"),
  ];
}

export function updatePlayerHistory(
  history: Position[],
  pos: Position,
): void {
  history.push({ x: pos.x, y: pos.y });
  if (history.length > HISTORY_MAX) history.shift();
}
